import posthog from "posthog-js";

import { env } from "@/lib/env";
import type { SignupProduct } from "@/lib/product";

type ProductContext = {
  product: SignupProduct;
  workspace_id?: string;
};

type NotificationContext = ProductContext & {
  permission_state?: "default" | "denied" | "granted";
  platform: "native" | "web";
};

/** `ios_hint` is the static Share-sheet card; it sits outside the A/B split. */
type InstallPromptContext = {
  experiment_id: string;
  variant: "ember" | "slide" | "ios_hint";
};

/**
 * Typed catalogue of in-app product events. Mirrors the marketing-site helper
 * (`lib/features/marketing/client/events.ts`); both route to PostHog.
 * Autocapture already records clicks/pageviews — reserve this
 * for deliberate domain milestones used in funnels and activation metrics.
 *
 * To add an event: extend `AppEvent`, then call `trackEvent` at the success
 * site. Keep names snake_case and props flat + non-PII.
 */
export type AppEvent =
  | { name: "workspace_switched"; props: { workspace_id: string } }
  | { name: "project_created"; props: { project_id: string; source: string } }
  | { name: "estimate_created"; props: { estimate_id: string } }
  | { name: "invoice_created"; props: { invoice_id: string } }
  | {
      name: "campaign_sent";
      props: { campaign_id: string; recipient_count: number };
    }
  | { name: "rag_query_submitted"; props: { project_id: string } }
  | { name: "bank_transaction_confirmed"; props: { transaction_id: string } }
  | {
      name: "billing_cancel_reason";
      props: { workspace_id: string; reason: string; plan_tier: string };
    }
  | {
      name: "onboarding_step_viewed";
      props: ProductContext & { step_id: string };
    }
  | {
      name: "onboarding_step_completed";
      props: ProductContext & { step_id: string };
    }
  | {
      name: "onboarding_step_skipped";
      props: ProductContext & { step_id: string };
    }
  | {
      name: "onboarding_step_failed";
      props: ProductContext & { step_id: string; reason_code: string };
    }
  | { name: "onboarding_free_tier_card_shown"; props: ProductContext }
  | { name: "onboarding_free_tier_selected"; props: ProductContext }
  | {
      name: "checklist_viewed";
      props: ProductContext & { completed_count: number; total_count: number };
    }
  | {
      name: "checklist_step_selected";
      props: ProductContext & { step_id: string };
    }
  | { name: "checklist_dismissed"; props: ProductContext }
  | { name: "notification_prompted"; props: NotificationContext }
  | { name: "notification_granted"; props: NotificationContext }
  | { name: "notification_denied"; props: NotificationContext }
  | {
      name: "notification_settings_opened";
      props: Omit<NotificationContext, "permission_state">;
    }
  | { name: "number_setup_completed"; props: ProductContext }
  | { name: "number_setup_skipped"; props: ProductContext }
  | {
      name: "number_setup_failed";
      props: ProductContext & { reason_code: string };
    }
  | {
      name: "route_failure";
      props: ProductContext & {
        route_class: string;
        status_code?: number;
      };
    }
  // Install-prompt A/B funnel: shown -> engaged (browser dialog opened) ->
  // resolved (the dialog's answer). `dismissed` is the card's X or a swipe.
  | { name: "pwa_install_prompt_shown"; props: InstallPromptContext }
  | { name: "pwa_install_prompt_engaged"; props: InstallPromptContext }
  | {
      name: "pwa_install_prompt_resolved";
      props: InstallPromptContext & { outcome: "accepted" | "dismissed" };
    }
  | { name: "pwa_install_prompt_dismissed"; props: InstallPromptContext };

type EventName = AppEvent["name"];
type EventProps<N extends EventName> = Extract<AppEvent, { name: N }>["props"];

const APP_EVENT_NAMES: Record<EventName, true> = {
  workspace_switched: true,
  project_created: true,
  estimate_created: true,
  invoice_created: true,
  campaign_sent: true,
  rag_query_submitted: true,
  bank_transaction_confirmed: true,
  billing_cancel_reason: true,
  onboarding_step_viewed: true,
  onboarding_step_completed: true,
  onboarding_step_skipped: true,
  onboarding_step_failed: true,
  onboarding_free_tier_card_shown: true,
  onboarding_free_tier_selected: true,
  checklist_viewed: true,
  checklist_step_selected: true,
  checklist_dismissed: true,
  notification_prompted: true,
  notification_granted: true,
  notification_denied: true,
  notification_settings_opened: true,
  number_setup_completed: true,
  number_setup_skipped: true,
  number_setup_failed: true,
  route_failure: true,
  pwa_install_prompt_shown: true,
  pwa_install_prompt_engaged: true,
  pwa_install_prompt_resolved: true,
  pwa_install_prompt_dismissed: true,
};

const SAFE_PROPERTY_KEYS = new Set([
  "ai_share",
  "app_version",
  "campaign_id",
  "completed_count",
  "deployment",
  "estimate_id",
  "experiment_id",
  "from_pool",
  "invoice_id",
  "outcome",
  "permission_state",
  "platform",
  "product",
  "project_id",
  "reason_code",
  "recipient_count",
  "route_class",
  "source",
  "status_code",
  "step_id",
  "template",
  "total_count",
  "transaction_id",
  "variant",
  "viewport_class",
  "workspace_id",
]);

type SafePropertyValue = boolean | number | string | null;

export function isAppEventName(value: string): value is EventName {
  return Object.prototype.hasOwnProperty.call(APP_EVENT_NAMES, value);
}

export function sanitizeAnalyticsProperties(
  properties: Record<string, unknown>,
): Record<string, SafePropertyValue> {
  const safe: Record<string, SafePropertyValue> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!SAFE_PROPERTY_KEYS.has(key)) continue;
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "boolean" ||
      (typeof value === "number" && Number.isFinite(value))
    ) {
      safe[key] = value;
    }
  }
  return safe;
}

function viewportClass(): "desktop" | "narrow" | "phone" | "tablet" {
  if (typeof window === "undefined") return "desktop";
  if (window.innerWidth < 360) return "narrow";
  if (window.innerWidth < 600) return "phone";
  if (window.innerWidth < 1024) return "tablet";
  return "desktop";
}

export function trackEvent<N extends EventName>(
  name: N,
  props: EventProps<N>,
): void {
  if (!isAppEventName(name)) return;
  posthog.capture(
    name,
    sanitizeAnalyticsProperties({
      platform: "web",
      viewport_class: viewportClass(),
      app_version: env.NEXT_PUBLIC_APP_VERSION ?? "unknown",
      deployment: process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID ?? "local",
      ...props,
    }),
  );
}
